import React, { useState } from 'react'

import { isCosBrand } from '@pb/utils'
import { useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'

import { Comments } from './product-detail-overview/Comments'
import { Dimensions } from './product-detail-overview/Dimensions'
import { ProductRating } from './product-detail-overview/ProductRating'
import { ReviewFooter } from './product-detail-overview/ReviewFooter'
import { ShopperProfiles } from './product-detail-overview/ShopperProfiles'
import styles from './ProductDetail.scss'
import { Props, DimensionsValue, ShopperProfilesValue } from './types'

const ProductDetail: React.FC<Props> = ({
  data,
  rating: ratingProp,
  handleSubmitReviewDataToParent,
}) => {
  const [isAcceptTermsChecked, setIsAcceptTermsChecked] = useState<boolean>(false)
  const [isShowTermsError, setIsShowTermsError] = useState<boolean>(false)
  const [localRate, setLocalRate] = useState(ratingProp)
  const [commentText, setCommentText] = useState('')
  const [isShowRateError, setIsShowRateError] = useState<boolean>(false)
  const [dimensionsValues, setDimensionsValues] = useState<Array<DimensionsValue>>([])
  const [selectedValues, setSelectedValues] = useState({})
  const [selectedDimensionValues, setSelectedDimensionValues] = useState({})

  const user = useSelector(appSelectors.getUser)

  const handleRatingClick = (value: Number | string): void => {
    setLocalRate(+value)
  }

  const handleSelectChange = (profileLabel, newValue): void => {
    setSelectedValues((prevState) => ({ ...prevState, [profileLabel]: newValue }))
  }

  const handleDimensionClick = (id, value) => {
    if (!dimensionsValues || !data.catalogItem.dimensions) {
      return
    }

    const updateDimensionValue = (dimension) => {
      if (dimension.label === id) {
        const matchingDimension = data.catalogItem.dimensions.find((d) => d.label === id)
        if (matchingDimension) {
          const matchingValue = matchingDimension.values.find((v) => v.label === value)
          if (matchingValue) {
            return {
              label: id,
              value: matchingValue.id.toString(),
            }
          }
        }
      }
      return dimension
    }

    const updatedDimensionsValues = dimensionsValues.map(updateDimensionValue)

    setDimensionsValues(updatedDimensionsValues)

    setSelectedDimensionValues((prevSelectedValues) => ({
      ...prevSelectedValues,
      [id]: value,
    }))
  }

  const handleCheckBoxChange = (): void => {
    setIsAcceptTermsChecked((prevIsAcceptTermsChecked) => !prevIsAcceptTermsChecked)
    setIsShowTermsError(false)
  }

  if (data.catalogItem.dimensions !== null) {
    const dimensionsDefaultValue = data.catalogItem.dimensions.map((dimension) => {
      return {
        label: dimension.label,
        value: dimension.values[1].id.toString(),
      }
    })

    if (!dimensionsValues.length) setDimensionsValues(dimensionsDefaultValue)
  }

  const handleSubmitReview = (): void => {
    if (!localRate && !ratingProp) {
      setIsShowRateError(true)
    }

    if (!isAcceptTermsChecked) {
      setIsShowTermsError(true)
    }

    if ((!localRate && !ratingProp) || !isAcceptTermsChecked) {
      return
    }

    const shopperProfiles: Array<ShopperProfilesValue> = Object.entries(selectedValues)
      .filter(([, value]) => value != null && value !== '')
      .map(([code]) => ({
        id: null,
        code,
      }))

    const reviewData = {
      text: commentText,
      rating: !localRate ? ratingProp : localRate,
      acceptedTermsAndConditions: 'true',
      user: {
        firstName: user.firstName,
        lastName: user.lastName,
        externalId: user.businessPartnerId,
        emailAddress: user.email,
        shopperProfiles,
      },
      catalogItems: [
        {
          sku: data.catalogItem.sku,
        },
      ],
      dimensions: dimensionsValues.length
        ? dimensionsValues.map((item) => {
            return { value: +item.value }
          })
        : null,
    }

    if (handleSubmitReviewDataToParent) handleSubmitReviewDataToParent(reviewData)
  }

  return (
    <section className={styles.reviewProductDetail}>
      {!isCosBrand ? (
        <>
          <ProductRating
            data={data}
            localRate={localRate}
            ratingProp={ratingProp}
            handleRatingClick={handleRatingClick}
            isShowRateError={isShowRateError}
          />
          <Comments commentText={commentText} setCommentText={setCommentText} />
          {data && (
            <Dimensions
              data={data}
              handleDimensionClick={handleDimensionClick}
              selectedDimensionValues={selectedDimensionValues}
            />
          )}
          <ShopperProfiles
            data={data}
            selectedValues={selectedValues}
            handleSelectChange={handleSelectChange}
          />
          <ReviewFooter
            isAcceptTermsChecked={isAcceptTermsChecked}
            handleCheckBoxChange={handleCheckBoxChange}
            isShowTermsError={isShowTermsError}
            handleSubmitReview={handleSubmitReview}
          />
        </>
      ) : (
        <>
          <ProductRating
            data={data}
            localRate={localRate}
            ratingProp={ratingProp}
            handleRatingClick={handleRatingClick}
            isShowRateError={isShowRateError}
          />
          {data && (
            <Dimensions
              data={data}
              handleDimensionClick={handleDimensionClick}
              selectedDimensionValues={selectedDimensionValues}
            />
          )}
          <ShopperProfiles
            data={data}
            selectedValues={selectedValues}
            handleSelectChange={handleSelectChange}
          />
          <Comments commentText={commentText} setCommentText={setCommentText} />
          <ReviewFooter
            isAcceptTermsChecked={isAcceptTermsChecked}
            handleCheckBoxChange={handleCheckBoxChange}
            isShowTermsError={isShowTermsError}
            handleSubmitReview={handleSubmitReview}
          />
        </>
      )}
    </section>
  )
}

export default ProductDetail
